import { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "./api";

export function useBoards(clientId, user) {
  const [boards, setBoards] = useState([]);
  const [loading, setLoading] = useState(true);

  const params = useMemo(
    () => (user?.role === "admin" && clientId ? { client_id: clientId } : {}),
    [clientId, user?.role],
  );

  const load = useCallback(async () => {
    if (!clientId) {
      setBoards([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.get("/portal/boards", { params });
      setBoards(Array.isArray(data) ? data : []);
    } catch {
      setBoards([]);
    } finally {
      setLoading(false);
    }
  }, [clientId, params, user?.role]);

  useEffect(() => {
    load();
  }, [load]);

  const createBoard = useCallback(
    async (title = "Untitled board") => {
      const { data } = await api.post("/portal/boards", { title }, { params });
      setBoards((prev) => [data, ...prev.filter((b) => b.id !== data.id)]);
      return data;
    },
    [params],
  );

  const saveBoard = useCallback(
    async (boardId, { elements, title } = {}) => {
      const body = {};
      if (elements !== undefined) body.elements = elements;
      if (title !== undefined) body.title = title;
      const { data } = await api.patch(`/portal/boards/${boardId}`, body, { params });
      setBoards((prev) => {
        const next = prev.map((b) => (b.id === data.id ? data : b));
        next.sort((a, b) => new Date(b.updated_at || 0) - new Date(a.updated_at || 0));
        return next;
      });
      return data;
    },
    [params],
  );

  const deleteBoard = useCallback(
    async (boardId) => {
      await api.delete(`/portal/boards/${boardId}`, { params });
      setBoards((prev) => prev.filter((b) => b.id !== boardId));
    },
    [params],
  );

  return { boards, loading, load, createBoard, saveBoard, deleteBoard };
}
